import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CMPStep2EnvironmentalControls = ({ project, onComplete, onBack }) => {
  const [saving, setSaving] = useState(false);
  
  // Dust management measures
  const [dustMeasures, setDustMeasures] = useState([
    'watering_surfaces', 'covering_stockpiles', 'covered_transport',
    'limiting_drop_heights', 'progressive_cleanup', 'suspension_adverse_weather'
  ]);
  
  // Noise management measures
  const [noiseMeasures, setNoiseMeasures] = useState([
    'approved_hours', 'maintained_equipment', 'scheduling_high_noise',
    'locating_plant_away', 'switching_off_equipment', 'monitoring_vibration'
  ]);
  
  // Water controls
  const [waterControls, setWaterControls] = useState({
    primary: ['sediment_fencing', 'inlet_protection', 'stabilised_access', 'sediment_traps'],
    erosion: ['progressive_stabilisation', 'minimise_exposed', 'diversion_clean_water'],
    runoff: ['site_drainage', 'prevent_discharge', 'pumping_collected', 'sediment_removal'],
    public: ['prevent_tracking', 'prompt_cleanup', 'drainage_maintenance']
  });

  useEffect(() => {
    // Load existing data if available
    if (project?.cmp_data) {
      if (project.cmp_data.dust_measures) setDustMeasures(project.cmp_data.dust_measures);
      if (project.cmp_data.noise_measures) setNoiseMeasures(project.cmp_data.noise_measures);
      if (project.cmp_data.water_controls) setWaterControls(project.cmp_data.water_controls);
    }
  }, [project]);

  const toggleDustMeasure = (measure) => {
    setDustMeasures(prev => 
      prev.includes(measure) 
        ? prev.filter(m => m !== measure)
        : [...prev, measure]
    );
  };

  const toggleNoiseMeasure = (measure) => {
    setNoiseMeasures(prev => 
      prev.includes(measure) 
        ? prev.filter(m => m !== measure)
        : [...prev, measure]
    );
  };

  const toggleWaterControl = (category, control) => {
    setWaterControls(prev => ({
      ...prev,
      [category]: prev[category].includes(control)
        ? prev[category].filter(c => c !== control)
        : [...prev[category], control]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('opwmp_token');
      
      await axios.post(`${API}/projects/${project.id}/cmp/step-data`, {
        dust_measures: dustMeasures,
        noise_measures: noiseMeasures,
        water_controls: waterControls
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update step
      await axios.put(`${API}/projects/${project.id}/step`, {
        step_number: 3
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onComplete({ ...project, current_step: 3 });
    } catch (error) {
      console.error('Error saving environmental controls:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const dustOptions = [
    { key: 'watering_surfaces', label: 'Watering of exposed surfaces during dry or windy conditions' },
    { key: 'covering_stockpiles', label: 'Covering or stabilisation of stockpiled materials' },
    { key: 'covered_transport', label: 'Covered vehicle transport of fine or loose materials' },
    { key: 'limiting_drop_heights', label: 'Limiting drop heights during material handling' },
    { key: 'progressive_cleanup', label: 'Progressive clean-up of dust-generating debris' },
    { key: 'suspension_adverse_weather', label: 'Suspension or modification of works during adverse weather' }
  ];

  const noiseOptions = [
    { key: 'approved_hours', label: 'Works limited to approved construction hours' },
    { key: 'maintained_equipment', label: 'Use of well-maintained plant and equipment with noise controls' },
    { key: 'scheduling_high_noise', label: 'Scheduling of high-noise activities to minimise impacts' },
    { key: 'locating_plant_away', label: 'Locating fixed plant away from sensitive boundaries where practicable' },
    { key: 'switching_off_equipment', label: 'Switching off plant and equipment when not in use' },
    { key: 'monitoring_vibration', label: 'Monitoring and modification of works where vibration impacts are identified' }
  ];

  const waterOptions = {
    primary: [
      { key: 'sediment_fencing', label: 'Installation of sediment fencing along site boundaries and downgradient areas' },
      { key: 'inlet_protection', label: 'Protection of stormwater inlets using inlet control devices or filter media' },
      { key: 'stabilised_access', label: 'Provision of stabilised site access points to minimise tracking of sediment' },
      { key: 'sediment_traps', label: 'Installation of sediment traps or basins where site conditions require' }
    ],
    erosion: [
      { key: 'progressive_stabilisation', label: 'Progressive stabilisation of exposed surfaces (e.g. mulch, gravel, turf or geofabric)' },
      { key: 'minimise_exposed', label: 'Minimisation of exposed soil areas and staged works where practicable' },
      { key: 'diversion_clean_water', label: 'Diversion of clean stormwater away from disturbed areas' }
    ],
    runoff: [
      { key: 'site_drainage', label: 'Management of site drainage to direct runoff to appropriate sediment control measures' },
      { key: 'prevent_discharge', label: 'Prevention of uncontrolled discharge of water from the site' },
      { key: 'pumping_collected', label: 'Pumping of collected water through sediment controls prior to discharge where required' },
      { key: 'sediment_removal', label: 'Removal of accumulated sediment in accordance with approved disposal methods' }
    ],
    public: [
      { key: 'prevent_tracking', label: 'Prevention of sediment tracking onto roads and footpaths' },
      { key: 'prompt_cleanup', label: 'Prompt clean-up of sediment deposited in public areas' },
      { key: 'drainage_maintenance', label: 'Maintenance of adjacent drainage infrastructure during works' }
    ]
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        {/* Back button at top */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Environmental Controls</h2>
        <p className="text-gray-600 mb-8">
          Select the environmental management measures that will be implemented during construction.
        </p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Dust and Air Quality */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              3.7 Dust and Air Quality Management
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Dust and air quality impacts will be managed to minimise nuisance and potential health impacts.
              Select the management measures that will be implemented:
            </p>
            <div className="space-y-3">
              {dustOptions.map(option => (
                <label key={option.key} className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={dustMeasures.includes(option.key)}
                    onChange={() => toggleDustMeasure(option.key)}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Noise and Vibration */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              3.8 Noise and Vibration Management
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Construction activities will be managed to minimise noise and vibration impacts.
              Select the management measures that will be implemented:
            </p>
            <div className="space-y-3">
              {noiseOptions.map(option => (
                <label key={option.key} className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={noiseMeasures.includes(option.key)}
                    onChange={() => toggleNoiseMeasure(option.key)}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Water, Runoff and Sediment Control */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              3.9 Water, Runoff and Sediment Control
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Measures will be implemented to manage stormwater runoff and prevent sediment from leaving the site.
            </p>

            {/* Primary Controls */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Primary Sediment Controls</h4>
              <div className="space-y-2 pl-4">
                {waterOptions.primary.map(option => (
                  <label key={option.key} className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={waterControls.primary.includes(option.key)}
                      onChange={() => toggleWaterControl('primary', option.key)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Erosion Controls */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Erosion Control Measures</h4>
              <div className="space-y-2 pl-4">
                {waterOptions.erosion.map(option => (
                  <label key={option.key} className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={waterControls.erosion.includes(option.key)}
                      onChange={() => toggleWaterControl('erosion', option.key)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Runoff Management */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Runoff Management</h4>
              <div className="space-y-2 pl-4">
                {waterOptions.runoff.map(option => (
                  <label key={option.key} className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={waterControls.runoff.includes(option.key)}
                      onChange={() => toggleWaterControl('runoff', option.key)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Public Area Protection */}
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Public Area Protection</h4>
              <div className="space-y-2 pl-4">
                {waterOptions.public.map(option => (
                  <label key={option.key} className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={waterControls.public.includes(option.key)}
                      onChange={() => toggleWaterControl('public', option.key)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CMPStep2EnvironmentalControls;
